/**
 * Japanese dictionary for the whole public site.
 *
 * Keys are the exact English text shown on the page (whitespace collapsed).
 * When a visitor picks JP in the nav, DomTranslator swaps every matching piece of
 * text (and placeholder / aria-label / title / alt) for its Japanese value.
 *
 * Adding copy: when you change English text on the site, add or update its entry
 * here. Missing entries simply stay in English. In development, untranslated text
 * on the current page is listed with `window.__i18nMisses()` in the browser console.
 *
 * Brand, product, people and company names are intentionally left in English.
 */

export const JA = {
  // ── Navigation & footer ───────────────────────────────────────────────
  "Dashboard": "ダッシュボード",
  "Transparency Dashboard": "透明性ダッシュボード",
  "Fund Metrics": "ファンド指標",
  "Deal flow, acceleration and investment": "ディールフロー、アクセラレーション、投資",
  "Ecosystem Metrics": "エコシステム指標",
  "Live Cardano on-chain data": "Cardanoのリアルタイムオンチェーンデータ",
  "Portfolio": "ポートフォリオ",
  "Companies backed by the fund": "ファンドの投資先企業",
  "Growth": "グロース",
  "Acceleration": "アクセラレーション",
  "Team": "チーム",
  "Media": "メディア",
  "Contact": "お問い合わせ",
  "Orion Fund home": "Orion Fund ホーム",
  "Toggle menu": "メニューを開閉",
  "Toggle theme": "テーマを切り替え",
  "Toggle currency": "通貨を切り替え",
  "Language": "言語",
  "Fund": "ファンド",
  "Company": "会社情報",
  "Follow us": "フォロー",
  "Orion Fund on X": "Orion Fund（X）",
  "Draper Dragon on X": "Draper Dragon（X）",
  "Legal": "法的情報",
  "Terms of Service": "利用規約",
  "Privacy Policy": "プライバシーポリシー",
  "Terms": "利用規約",
  "Privacy": "プライバシー",
  "Cardano's first professionally managed Ecosystem Fund, led by Draper Dragon.": "Draper Dragonが運営する、Cardano初のプロフェッショナルなエコシステムファンド。",
  "Important disclosures": "重要な開示事項",
  "Full terms →": "規約全文 →",
  "Orion Fund · Draper Dragon. All rights reserved.": "Orion Fund · Draper Dragon. All rights reserved.",
  "Provide Feedback": "フィードバックを送る",
  "Feedback": "フィードバック",
  "FEEDBACK": "フィードバック",

  // Footer disclosures (reference translation; the English version governs)
  "No Offer, Recommendation or Solicitation": "募集・推奨・勧誘ではありません",
  "Nothing on this site constitutes an offer to sell, a solicitation of an offer to buy, or a recommendation of any security, digital asset, fund interest or other financial instrument by Draper Dragon Orion Fund, Draper Dragon or any of their affiliates (together, \"Orion\", \"we\", \"us\" or \"our\"). Any such offer may only be made to qualified investors through definitive offering documents, and only in jurisdictions where it is lawful to do so.":
    "本サイトのいかなる内容も、Draper Dragon Orion Fund、Draper Dragonまたはそれらの関係会社（以下総称して「Orion」または「当社」）による、有価証券、デジタル資産、ファンド持分その他の金融商品の売付けの申込み、買付けの申込みの勧誘、または推奨を構成するものではありません。そのような申込みは、正式な募集書類を通じて適格投資家に対してのみ、かつ適法な法域においてのみ行われます。",
  "Not Investment, Legal or Tax Advice": "投資・法務・税務上の助言ではありません",
  "The information on this site, including dashboard metrics, portfolio information and program descriptions, is provided for general informational and transparency purposes only. It is not investment, legal, tax or accounting advice. You should consult your own advisors before making any decision.":
    "ダッシュボードの指標、ポートフォリオ情報、プログラムの説明を含む本サイトの情報は、一般的な情報提供および透明性確保のみを目的としています。投資、法務、税務、会計上の助言ではありません。意思決定の前にご自身の専門家にご相談ください。",
  "Forward-Looking Statements": "将来予想に関する記述",
  "Certain statements on this site, in our social media posts or in other public communications may be forward-looking. Words such as \"may\", \"will\", \"should\", \"expect\", \"anticipate\", \"estimate\", \"intend\", \"plan\", \"target\" and similar expressions identify these statements. They reflect current expectations only, involve risks and uncertainties, and actual results may differ materially. We undertake no obligation to update them.":
    "本サイト、当社のソーシャルメディア投稿その他の公開情報に含まれる一部の記述は、将来予想に関する記述である場合があります。「可能性がある」「予定」「見込み」「予想」「目標」等の表現はこうした記述を示します。これらは現時点の見通しにすぎず、リスクと不確実性を伴い、実際の結果は大きく異なる可能性があります。当社はこれらを更新する義務を負いません。",
  "Data & Third-Party Sources": "データおよび第三者情報源",
  "On-chain metrics and prices are sourced from third parties including Dune Analytics, DefiLlama, Blockfrost, Binance and CoinGecko. This data has not been independently verified, may be delayed or incomplete, and may change without notice. Past activity and ecosystem metrics are not indicative of future results.":
    "オンチェーン指標および価格は、Dune Analytics、DefiLlama、Blockfrost、Binance、CoinGeckoなどの第三者から取得しています。これらのデータは独自に検証されておらず、遅延や欠落がある場合があり、予告なく変更されることがあります。過去の実績やエコシステム指標は将来の結果を示すものではありません。",
  "Social Media Disclaimer": "ソーシャルメディアに関する免責事項",
  "Content we publish on X, LinkedIn and other channels is provided solely for informational purposes and is subject to the same disclaimers as this site. Orion, Draper Dragon and their affiliates, partners and personnel may hold positions in companies, digital assets or protocols we discuss.":
    "X、LinkedInその他のチャネルで当社が発信するコンテンツは情報提供のみを目的とし、本サイトと同じ免責事項が適用されます。Orion、Draper Dragonおよびその関係会社、パートナー、役職員は、言及する企業、デジタル資産またはプロトコルのポジションを保有している場合があります。",

  // ── Home ──────────────────────────────────────────────────────────────
  "Accelerating the next phase of": "Cardano普及の",
  "Cardano adoption": "次のフェーズを加速する",
  "Led by Draper Dragon, Orion Fund supports both Cardano-native builders and companies integrating with Cardano, combining investment with active support to help them grow, scale, and drive adoption.":
    "Draper Dragonが運営するOrion Fundは、Cardanoネイティブのビルダーと、Cardanoとの統合を進める企業の双方を支援しています。投資と積極的なサポートを組み合わせ、成長・スケール・普及の推進を後押しします。",
  "Meet the Team": "チームを見る",
  "Get in Touch": "お問い合わせ",
  "01 — Overview": "01 — 概要",
  "The Orion Fund is a multi-year venture fund led by": "Orion Fundは、",
  ", backing companies at the intersection of": "が運営する複数年にわたるベンチャーファンドです。",
  "blockchain infrastructure": "ブロックチェーン・インフラ",
  "and real-world adoption.": "と実社会での普及が交わる領域の企業に投資しています。",
  "How we support": "支援の方法",
  "Through direct investment, incubations, and accelerator programs with Draper University, we give founders the capital, expertise, and connections to build and scale.":
    "直接投資、インキュベーション、そしてDraper Universityとのアクセラレーター・プログラムを通じて、創業者が事業を構築・拡大するための資金、専門知識、ネットワークを提供します。",
  "Who we back": "投資対象",
  "We support Cardano-native teams and welcome companies from outside the ecosystem, helping them explore and develop meaningful Cardano integrations. Our goal is to strengthen existing businesses and bring new builders, products, and users into Cardano.":
    "Cardanoネイティブのチームを支援するとともに、エコシステム外の企業も歓迎し、意義あるCardano統合の検討・開発をサポートします。既存事業を強化し、新たなビルダー、プロダクト、ユーザーをCardanoに呼び込むことが私たちの目標です。",
  "02 — Our model": "02 — 私たちのモデル",
  "03 — Build with us": "03 — 共に築く",
  "Building something ambitious?": "大きな挑戦をしていますか？",
  "Let's talk.": "ぜひお話ししましょう。",
  "Funding": "投資",
  "Capital for ambitious founders": "志ある創業者への資金提供",
  "We invest in promising companies building useful products and scalable businesses. We back both Cardano-native teams and companies entering the ecosystem, connecting founders with capital, strategic partners, and the broader Draper network to help them scale.":
    "有用なプロダクトとスケーラブルな事業を築く有望な企業に投資します。Cardanoネイティブのチームとエコシステムに参入する企業の双方を支援し、資金、戦略パートナー、そしてDraperネットワークへとつなぎ、成長を後押しします。",
  "Technical mentorship and company creation": "技術メンタリングと企業創出",
  "Our in-house venture studio, Cometa Labs, provides technical mentorship to portfolio companies and incubates new businesses from the ground up. The team works alongside founders on product development, Cardano integrations, and go-to-market execution, while building companies that create opportunities for equity ownership and long-term financial upside for Orion and the Cardano ecosystem.":
    "社内ベンチャースタジオのCometa Labsは、投資先企業への技術メンタリングを行うとともに、新規事業をゼロからインキュベートします。プロダクト開発、Cardano統合、市場投入の実行において創業者と並走し、Orionとエコシステムにとって株式保有と長期的なリターンの機会を生む企業を築いています。",
  "Founder development with Draper University": "Draper Universityとの創業者育成",
  "Through our partner Draper University, we offer accelerator programs and Silicon Valley residencies that connect founders with experienced operators, mentors, and investors. Our goal is to bring new builders into Cardano and help existing teams grow, refine their products, and prepare for their next stage of funding.":
    "パートナーであるDraper Universityを通じて、アクセラレーター・プログラムとシリコンバレーでのレジデンシーを提供し、創業者を経験豊富なオペレーター、メンター、投資家とつなぎます。新たなビルダーをCardanoに迎え入れ、既存チームの成長、プロダクトの磨き込み、次の資金調達への準備を支援します。",
  "Draper Dragon (opens in a new tab)": "Draper Dragon（新しいタブで開く）",

  // ── Dashboard page ─────────────────────────────────────────────────────
  "Transparency": "透明性",
  "The Orion Fund": "Orion Fund",
  "dashboard": "ダッシュボード",
  "Capital deployed": "投資実績",
  "Companies Backed": "投資先企業数",
  "Direct investments by the Orion Fund · View portfolio": "Orion Fundによる直接投資 · ポートフォリオを見る",
  "Outside Capital Raised": "外部資金調達額",
  "Raised by the Orion Fund to date": "Orion Fundのこれまでの調達額",
  "Cardano Integrations": "Cardano統合",
  "Direct + Draper University accelerator · View portfolio": "直接投資＋Draper Universityアクセラレーター · ポートフォリオを見る",
  "Cometa Labs venture studio": "Cometa Labs ベンチャースタジオ",
  "Technical mentorship and company creation.": "技術メンタリングと企業創出。",
  "Teams Mentored": "メンタリングしたチーム",
  "Incubated Companies": "インキュベーション企業",
  "Products Launched": "ローンチしたプロダクト",
  "Companies Spun Out": "スピンアウトした企業",
  "Draper University programs": "Draper University プログラム",
  "Founder development through the Genesis and Apex accelerators.": "GenesisとApexのアクセラレーターによる創業者育成。",
  "Companies Accelerated": "アクセラレーション企業数",
  "Mentorship Sessions Delivered": "実施したメンタリング",
  "Investor Meetings Facilitated": "設定した投資家ミーティング",
  "Ecosystem": "エコシステム",
  "The broader Cardano network the fund invests in.": "ファンドが投資するCardanoネットワーク全体。",
  "ADA price": "ADA価格",
  "Live, refreshed every few minutes": "リアルタイム、数分ごとに更新",
  "Total value locked": "預かり資産総額（TVL）",
  "Across Cardano DeFi": "Cardano DeFi全体",
  "Monthly active users": "月間アクティブユーザー",
  "Avg. daily active wallets": "1日平均アクティブウォレット数",
  "LIVE": "ライブ",
  "Live on-chain metrics via Dune": "Duneによるリアルタイムのオンチェーン指標",
  "Timeframe": "期間",
  "Portfolio companies": "投資先企業",
  "Select a company to see what they're building.": "企業を選択すると事業内容を表示します。",
  "Companies": "社",
  "Genesis Cohort 1": "Genesis 第1期",
  "About": "概要",
  "Links": "リンク",
  "Website coming soon.": "ウェブサイトは近日公開予定です。",
  "Disclosure Note": "開示に関する注記",
  "Expand +": "開く +",
  "Collapse −": "閉じる −",
  "Scope of Public Reporting:": "公開報告の範囲：",
  "This dashboard reports ecosystem-level, operational, and publicly disclosable fund information. Confidential portfolio terms, valuations, and transaction details may be withheld where required by law, regulation, or fund confidentiality obligations.":
    "本ダッシュボードは、エコシステム全体の情報、運営情報、および開示可能なファンド情報を報告するものです。法令、規制またはファンドの守秘義務により必要な場合、投資条件、評価額、取引の詳細は開示しないことがあります。",
  "Not an Offer or Solicitation:": "募集・勧誘ではありません：",
  "This dashboard is for informational and transparency purposes only. It does not constitute an offer to sell or a solicitation to buy any securities or investment products.":
    "本ダッシュボードは情報提供と透明性確保のみを目的としています。いかなる有価証券または投資商品の売付けの申込みまたは買付けの勧誘を構成するものではありません。",
  "No Performance Guarantees:": "成果の保証はありません：",
  "Past deployment activity and ecosystem metrics do not guarantee future results. All investments involve risk, including possible loss of principal.":
    "過去の投資実績やエコシステム指標は将来の結果を保証するものではありません。すべての投資には元本割れを含むリスクがあります。",
  "Data Freshness:": "データの鮮度：",
  "Dashboard data may not reflect real-time conditions. On-chain metrics, portfolio updates, and third-party data feeds may experience delays ranging from minutes to days. Users should not rely on this dashboard for time-sensitive trading or any investment decisions.":
    "ダッシュボードのデータはリアルタイムの状況を反映していない場合があります。オンチェーン指標、ポートフォリオ情報、第三者データには数分から数日の遅延が生じることがあります。時間的制約のある取引や投資判断に本ダッシュボードを用いないでください。",
  "ADA/USD Conversions:": "ADA/USDの換算：",
  "All ADA to USD conversions use the current ADA price fetched live from the CoinGecko API. Historical values are converted using today's exchange rate, not the rate at the time of the data point.":
    "ADAからUSDへの換算はすべて、CoinGecko APIから取得した現在のADA価格を使用しています。過去の数値も、各時点のレートではなく本日のレートで換算しています。",
  "Methodology:": "算出方法：",
  "Metrics are calculated based on observable on-chain data, portfolio company reporting, and fund operational records. Ecosystem data is sourced from third-party platforms including Blockfrost (Cardano blockchain data) and Dune Analytics (on-chain analytics). Detailed methodology documentation is available upon request.":
    "指標は、観測可能なオンチェーンデータ、投資先企業からの報告、ファンドの運営記録に基づいて算出しています。エコシステムのデータは、Blockfrost（Cardanoブロックチェーンデータ）やDune Analytics（オンチェーン分析）などの第三者プラットフォームから取得しています。詳細な算出方法はご要望に応じて提供します。",
  "Edit": "編集",
  "Other": "その他",
  "Awaiting live data": "ライブデータを待機中",
  "Wallets:": "ウォレット数：",
  "All-time": "全期間",
  "Period": "期間",
  "YoY": "前年比",
  "Daily active wallets counts unique wallet addresses that submit at least one transaction each day on Cardano.":
    "1日あたりアクティブウォレット数は、Cardano上で1日に1件以上のトランザクションを送信したユニークなウォレットアドレスの数です。",
  "Daily active wallets counts unique wallet addresses that submit at least one transaction each day, reflecting real user engagement on-chain. Orion-backed dApps, payments platforms, and integrations are designed to drive this adoption.":
    "1日あたりアクティブウォレット数は、1日に1件以上のトランザクションを送信したユニークなウォレットアドレスの数で、オンチェーンでの実際のユーザー利用を示します。Orionが支援するdApp、決済プラットフォーム、統合はこの普及を後押しするよう設計されています。",
  "wallet growth (first vs last data point)": "ウォレット数の伸び（最初と最新のデータ点の比較）",
  "Protocol revenue tracks ADA transaction fees collected by the Cardano network each day.":
    "プロトコル収益は、Cardanoネットワークが毎日徴収するADA建てのトランザクション手数料です。",
  "Protocol revenue tracks ADA transaction fees collected by the Cardano network each day, reflecting the ecosystem's path toward financial self-sufficiency. Orion's portfolio of revenue-generating projects in payments, RWA, and DeFi directly contributes to fee volume.":
    "プロトコル収益は、Cardanoネットワークが毎日徴収するADA建てのトランザクション手数料で、エコシステムの財務的自立への歩みを示します。決済、RWA、DeFiで収益を生むOrionのポートフォリオは、手数料の増加に直接貢献します。",
  "revenue growth (first vs last data point)": "収益の伸び（最初と最新のデータ点の比較）",
  "TVL measures the total value of assets locked across Cardano's DeFi protocols, RWA platforms, and liquidity pools.":
    "TVLは、CardanoのDeFiプロトコル、RWAプラットフォーム、流動性プールにロックされた資産の総額です。",
  "TVL measures the total value of assets locked across Cardano's DeFi protocols, RWA platforms, and liquidity pools. Orion's investments in RWA and institutional DeFi are directly designed to contribute to this growth.":
    "TVLは、CardanoのDeFiプロトコル、RWAプラットフォーム、流動性プールにロックされた資産の総額です。OrionのRWAおよび機関投資家向けDeFiへの投資は、この成長に直接貢献するよう設計されています。",
  "TVL change (first vs last data point)": "TVLの変化（最初と最新のデータ点の比較）",
  "Daily on-chain transactions measures the number of submitted Cardano transactions each day.":
    "1日あたりオンチェーントランザクション数は、Cardano上で毎日送信されたトランザクションの件数です。",
  "Daily on-chain transactions measures the number of submitted Cardano transactions each day, serving as a direct signal of real-world utility and network demand. Orion-backed projects in payments, RWA, and DeFi are direct contributors.":
    "1日あたりオンチェーントランザクション数は、Cardano上で毎日送信されたトランザクションの件数で、実社会での有用性とネットワーク需要を直接示す指標です。決済、RWA、DeFiにおけるOrionの支援プロジェクトが直接貢献しています。",

  // Portfolio (sectors and descriptions; company names stay in English)
  "Consumer": "コンシューマー",
  "Payments": "決済",
  "Infrastructure": "インフラ",
  "Tokenized real-world assets — borrow against your stocks on-chain.": "トークン化された現実資産 — 保有株式を担保にオンチェーンで借入。",
  "The fiduciary layer for agentic payments.": "エージェント決済のための受託レイヤー。",
  "Fractional real estate investing, made simple.": "不動産の小口投資を、もっとシンプルに。",
  "A fractional real estate investing platform founded by Goldman Sachs alumni, letting investors own a share of real estate with monthly payouts.":
    "ゴールドマン・サックス出身者が創業した不動産小口投資プラットフォーム。投資家は不動産の持分を保有し、毎月分配を受け取れます。",
  "Privacy-preserving swaps and dark pools for bitcoin exchanges.": "ビットコイン取引所向けの、プライバシーを保護するスワップとダークプール。",
  "LX builds bitcoin dark-pool infrastructure — the easiest way for exchanges to add privacy-preserving swaps.":
    "LXはビットコインのダークプール基盤を構築しています。取引所がプライバシー保護型スワップを導入する最も簡単な方法です。",
  "Trustless Bitcoin lending — borrow dollars against your BTC in one block.": "トラストレスなビットコイン・レンディング — 1ブロックでBTCを担保にドルを借入。",
  "A trustless lending protocol enabling instant, Bitcoin-backed dollar loans on a decentralized platform.":
    "分散型プラットフォーム上で、ビットコインを担保とするドル建てローンを即時に実行できるトラストレスなレンディング・プロトコル。",
  "Circle's open Layer-1 blockchain purpose-built for stablecoin finance.": "ステーブルコイン金融のために設計された、Circleのオープンなレイヤー1ブロックチェーン。",
  "Arc is the open Layer-1 blockchain from Circle, the issuer of USDC, built as an economic operating system for stablecoin finance on the internet.":
    "Arcは、USDC発行体であるCircleによるオープンなレイヤー1ブロックチェーンで、インターネット上のステーブルコイン金融のための経済OSとして構築されています。",
  "Institutional Bitcoin yield, anchored by miner credit and productive markets.": "マイナー与信と生産的な市場に裏付けられた、機関投資家向けビットコイン利回り。",
  "Maestro delivers Bitcoin yield instruments through structured vaults with delta-neutral hedging, backed by miner credit and productive Bitcoin markets.":
    "Maestroは、デルタニュートラルのヘッジを用いたストラクチャード・ボールトを通じて、マイナー与信と生産的なビットコイン市場に裏付けられた利回り商品を提供します。",
  "Pay-per-view marketplace connecting brands with content creators.": "ブランドとコンテンツクリエイターをつなぐ、再生課金型マーケットプレイス。",
  "ClipStake connects brands with content creators through pay-per-view campaigns, so brands pay for real views.":
    "ClipStakeは再生課金型キャンペーンでブランドとクリエイターをつなぎ、ブランドは実際の再生数に対してのみ支払います。",
  "Verifiable attestation infrastructure for non-price, real-world data.": "価格以外の現実データのための、検証可能なアテステーション基盤。",
  "An attestation layer turning public records into on-chain facts.": "公的記録をオンチェーンの事実に変えるアテステーション・レイヤー。",
  "Authorized procurement layer for AI agents accessing gated content and services.": "AIエージェントが限定コンテンツやサービスを正規に調達するためのレイヤー。",
  "A procurement and entitlement layer for AI agents: finding an authorized source, checking entitlements and delivering content with rights, provenance and a receipt. Formerly Agent-to-agent.xyz.":
    "AIエージェントのための調達・権利管理レイヤー。正規の提供元を見つけ、利用権限を確認し、権利情報・来歴・受領証付きでコンテンツを届けます。旧Agent-to-agent.xyz。",
  "Onboarding, discovery, and distribution hub for the Cardano ecosystem.": "Cardanoエコシステムのオンボーディング、発見、流通のハブ。",
  "A discovery and onboarding hub connecting users and Cardano apps.": "ユーザーとCardanoアプリをつなぐ、発見とオンボーディングのハブ。",
  "White-label WhatsApp payments infrastructure for fintechs, now live on Cardano.": "フィンテック向けホワイトラベルのWhatsApp決済基盤。Cardanoで稼働中。",
  "A WhatsApp-native crypto payments SDK for fintechs.": "フィンテック向けの、WhatsAppネイティブな暗号資産決済SDK。",
  "All-in-one operating system for mid-market conferences and trade shows.": "中規模カンファレンスや展示会のためのオールインワンOS。",
  "A unified event OS for ticketing, registration, CRM, sponsors, floor plans, communications and analytics — replacing the 5–8 tools organizers typically juggle.":
    "チケット販売、登録、CRM、スポンサー、フロアプラン、コミュニケーション、分析をまとめた統合イベントOS。主催者が使い分けている5〜8個のツールを置き換えます。",
  "Censorship-resistant Cardano DEX with no operator-controlled server dependency.": "運営者が管理するサーバーに依存しない、検閲耐性のあるCardano DEX。",
  "A Cardano DEX that runs without operator-controlled servers.": "運営者管理のサーバーなしで動作するCardano DEX。",
  "Liquid staking standard for Cardano — stake ADA, mint L-ADA, and deploy it across DeFi.": "Cardanoのリキッドステーキング標準 — ADAをステークしてL-ADAを発行し、DeFiで活用。",
  "A liquid staking token (L-ADA) for Cardano DeFi.": "Cardano DeFiのためのリキッドステーキング・トークン（L-ADA）。",
  "Unified workspace and AI orchestration layer for small Web3 teams.": "小規模なWeb3チームのための統合ワークスペースとAIオーケストレーション・レイヤー。",
  "An AI-powered unified workspace for Web3 teams.": "Web3チームのためのAI搭載統合ワークスペース。",
  "Powering compliance for a global digital economy.": "グローバルなデジタル経済のコンプライアンスを支える。",
  "A modular compliance platform that helps crypto-asset companies and financial institutions automate Travel Rule and AML/CTF requirements across multiple blockchains.":
    "暗号資産事業者や金融機関が、複数のブロックチェーンにわたるトラベルルールおよびAML/CTF要件を自動化できるモジュール型コンプライアンス・プラットフォーム。",
  "Bitcoin Lightning payments with dollar settlement for unattended retail.": "無人販売向けの、ドル決済に対応したビットコイン・ライトニング決済。",
  "Lightning-to-dollar payments for vending machines and kiosks.": "自動販売機やキオスク向けの、ライトニングからドルへの決済。",
  "Stablecoin-powered payments, cashback, and loyalty for South African retailers.": "南アフリカの小売事業者向け、ステーブルコインによる決済・キャッシュバック・ロイヤルティ。",
  "Stablecoin cashback and loyalty for South African retailers.": "南アフリカの小売事業者向け、ステーブルコインのキャッシュバックとロイヤルティ。",
  "Creating scalable solutions on Cardano from escrow services to trusted stablecoin payments.": "エスクローから信頼性の高いステーブルコイン決済まで、Cardano上でスケーラブルなソリューションを構築。",
  "Creating scalable solutions on Cardano from escrow services to trusted stablecoin payments. Products include Web3 Bounties, an on-chain escrow engine guaranteeing contributors get paid for freelance and bounty work.":
    "エスクローから信頼性の高いステーブルコイン決済まで、Cardano上でスケーラブルなソリューションを構築しています。プロダクトには、フリーランスやバウンティの報酬支払いを保証するオンチェーン・エスクローエンジン「Web3 Bounties」があります。",

  // ── Growth (Cometa Labs) ───────────────────────────────────────────────
  "Growth · In-house venture studio": "グロース · 社内ベンチャースタジオ",
  "Building and incubating early-stage crypto-native products and infrastructure, from ideation to scale.":
    "アイデアからスケールまで、アーリーステージのクリプトネイティブなプロダクトとインフラを構築・インキュベートします。",
  "01 — About": "01 — 概要",
  "Who We Are": "私たちについて",
  "Cometa Labs is a venture studio run by founders, engineers and product experts who have taken a company from first idea to acquisition.":
    "Cometa Labsは、企業をアイデアの段階から買収まで導いた経験を持つ創業者、エンジニア、プロダクトの専門家が運営するベンチャースタジオです。",
  "We start companies and incubate them in-house. Product, engineering, brand, market testing and venture support all sit inside the studio, so a new idea has what it needs from the first week.":
    "私たちは自ら企業を立ち上げ、社内でインキュベートします。プロダクト、エンジニアリング、ブランド、市場検証、ベンチャー支援のすべてがスタジオ内にそろっているため、新しいアイデアは最初の週から必要なものを手にできます。",
  "Between us, we've built across Web3, AI, fintech, data analytics, gaming and digital ownership. That includes founding Scarcity Labs and leading it through its acquisition by DraftKings.":
    "私たちはWeb3、AI、フィンテック、データ分析、ゲーム、デジタル所有権の分野で事業を築いてきました。その中には、Scarcity Labsを創業し、DraftKingsによる買収まで導いた実績も含まれます。",
  "Today, we bring that experience back to the earliest stage of company building: identifying opportunities, rapidly turning ideas into working products, testing them in the real world, and building the teams and foundations they need to become independent companies.":
    "現在はその経験を企業づくりの最初期に生かしています。機会を見極め、アイデアをすばやく動くプロダクトにし、実社会で検証し、独立した企業となるためのチームと基盤を築きます。",
  "Our Mission": "ミッション",
  "To turn ambitious ideas into lasting technology companies.": "大胆なアイデアを、長く続くテクノロジー企業へ。",
  "What usually separates a good idea from a real company is time, money and the right people. We close that gap by putting capital, engineering, product thinking and execution to work on the idea at the same time.":
    "良いアイデアと本物の企業を隔てるのは、たいてい時間、資金、そして適切な人材です。私たちは資金、エンジニアリング、プロダクト思考、実行力を同時にアイデアへ投入することで、その差を埋めます。",
  "We move quickly, test with real users and double down on what works. When a venture is ready, we spin it out with its own team, funding and infrastructure to scale.":
    "すばやく動き、実際のユーザーで検証し、うまくいくものに集中します。準備が整えば、独自のチーム、資金、インフラを備えた企業としてスピンアウトさせ、スケールさせます。",
  "Sometimes that means building alongside a founder. Sometimes it means starting before there is one.":
    "創業者と共に築くこともあれば、創業者がいない段階から始めることもあります。",
  "Visit Cometa Labs": "Cometa Labsを見る",
  "Cometa Labs on X": "Cometa Labs（X）",
  "02 — Incubation schedule": "02 — インキュベーションの流れ",
  "From first idea to": "最初のアイデアから",
  "scaled product": "スケールするプロダクトへ",
  "Ideate & Validate": "アイデア創出と検証",
  "We identify promising markets and unmet needs, then validate the opportunity through research, rapid experimentation, and ecosystem feedback.":
    "有望な市場と満たされていないニーズを見極め、リサーチ、迅速な実験、エコシステムからのフィードバックを通じて機会を検証します。",
  "Build the MVP": "MVPの構築",
  "Our product and engineering teams design the architecture, prototype quickly, and build a functional MVP ready for real users and real world testing.":
    "プロダクトとエンジニアリングのチームがアーキテクチャを設計し、すばやくプロトタイプを作り、実際のユーザーと実環境で検証できるMVPを構築します。",
  "Test & Refine": "検証と改善",
  "We put products in front of users, measure traction, and use real feedback and data to refine the product, business model, and positioning before scaling.":
    "プロダクトをユーザーに届けてトラクションを測り、実際のフィードバックとデータをもとに、スケール前にプロダクト、ビジネスモデル、ポジショニングを磨きます。",
  "Launch & Spin Out": "ローンチとスピンアウト",
  "Once the product shows potential, we develop its brand and go-to-market strategy, recruit dedicated leadership, establish the venture structure, and prepare it for independent growth.":
    "プロダクトに可能性が見えたら、ブランドと市場投入戦略をつくり、専任の経営陣を迎え、事業体制を整えて、独立した成長に備えます。",
  "Scale": "スケール",
  "We help ventures grow through fundraising, strategic partnerships, talent, marketing, and access to the Draper ecosystem while continuing to provide strategic and technical support.":
    "資金調達、戦略的パートナーシップ、人材、マーケティング、Draperエコシステムへのアクセスを通じて成長を支援し、戦略面・技術面のサポートも継続します。",
  "Previous step": "前のステップ",
  "Next step": "次のステップ",
  "DeFi infrastructure": "DeFiインフラ",
  "Marketplaces": "マーケットプレイス",
  "Consumer crypto": "コンシューマー向けクリプト",
  "Financial primitives": "金融プリミティブ",
  "AI × blockchain": "AI × ブロックチェーン",

  // ── Acceleration ────────────────────────────────────────────────────────
  "Accelerator Programs": "アクセラレーター・プログラム",
  "Join Draper University in Silicon Valley and build your blockchain startup on Cardano.":
    "シリコンバレーのDraper Universityで、Cardano上にあなたのブロックチェーン・スタートアップを築きましょう。",
  "00 — The programs": "00 — プログラム",
  "Draper University, in partnership with Cardano and Draper Dragon, introduces the DraperU Cardano Accelerator Ecosystem Programs. We are seeking high-impact Web3 founders to drive global adoption on the Cardano blockchain. Join us in Silicon Valley for an intensive elite residency, strategic capital, and direct access to institutional VCs and the global Draper network.":
    "Draper Universityは、CardanoおよびDraper Dragonとの提携により、DraperU Cardanoアクセラレーター・エコシステム・プログラムを開始します。Cardanoブロックチェーンのグローバルな普及を牽引する、インパクトの大きいWeb3創業者を募集しています。シリコンバレーでの集中的なレジデンシー、戦略的資金、そして機関投資家VCとグローバルなDraperネットワークへの直接のアクセスを提供します。",
  "All participating startups gain direct access to, and strategic support from, the newly minted $80M Draper Dragon Orion Fund to supercharge their institutional scale.":
    "参加するすべてのスタートアップは、新たに組成された8,000万ドル規模のDraper Dragon Orion Fundへの直接のアクセスと戦略的支援を受け、本格的な事業拡大を加速できます。",
  "Visit Draper University": "Draper Universityを見る",
  "Draper University on X": "Draper University（X）",
  "Cohort Closed": "募集終了",
  "Applications open": "募集中",
  "Investment": "投資額",
  "Equity": "株式",
  "Cohort": "定員",
  "What's included": "プログラム内容",
  "Expand": "開く",
  "Collapse": "閉じる",
  "Pre Accelerator": "プレ・アクセラレーター",
  "For Early-Stage Technical Builders": "アーリーステージの技術系ビルダー向け",
  "4 Weeks (+4 Extension)": "4週間（＋延長4週間）",
  "Up to $20,000": "最大2万ドル",
  "2% target equity": "目標株式2%",
  "Up to 15 teams": "最大15チーム",
  "Applications Due": "応募締切",
  "Final Offers": "最終オファー",
  "Program Start Date": "プログラム開始日",
  "The \"Genesis\" Pre Accelerator is a 4-week intensive Silicon Valley program for 15 early-stage technical teams. Selected teams receive up to $20,000 in investment for a target of 2% equity, full residency at Draper University, and direct access to experienced founders, operators, and investors. The program concludes with a dedicated Pitch Day, giving teams the opportunity to present to angel investors and early-stage VCs.":
    "「Genesis」プレ・アクセラレーターは、アーリーステージの技術系チーム15組を対象とした、シリコンバレーでの4週間の集中プログラムです。選抜チームは目標株式2%に対して最大2万ドルの投資、Draper Universityでのフルレジデンシー、経験豊富な創業者・オペレーター・投資家への直接のアクセスを得られます。プログラムの最後には専用のピッチデーがあり、エンジェル投資家やアーリーステージVCにプレゼンテーションする機会があります。",
  "Investment & Silicon Valley Residency": "投資とシリコンバレーでのレジデンシー",
  "Receive up to $20,000 in investment plus full residency at Draper University in Silicon Valley": "最大2万ドルの投資に加え、シリコンバレーのDraper Universityでのフルレジデンシーを提供",
  "Build Alongside Exceptional Founders": "優れた創業者と共に開発",
  "Join a cohort of ambitious technical builders working intensely alongside you in person": "意欲的な技術系ビルダーのコホートに加わり、対面で集中して共に取り組みます",
  "Rapid Product Validation": "迅速なプロダクト検証",
  "Move quickly from concept to validated product through focused mentorship, feedback, and execution support": "集中的なメンタリング、フィードバック、実行支援により、コンセプトから検証済みプロダクトへと迅速に進めます",
  "Learn to Think Like a Founder": "創業者の思考を身につける",
  "Develop the skills needed to transition from technical builder to venture-backed entrepreneur": "技術系ビルダーから、VCの支援を受ける起業家へと成長するためのスキルを養います",
  "Direct Access to Builders & Investors": "ビルダーと投資家への直接アクセス",
  "Work closely with experienced founders, operators, and early-stage investors throughout the program": "プログラムを通じて、経験豊富な創業者、オペレーター、アーリーステージ投資家と密接に協働します",
  "Pitch Day with Angels & VCs": "エンジェル・VC向けピッチデー",
  "Present your company to a curated audience of angel investors and early-stage venture capital firms": "厳選されたエンジェル投資家とアーリーステージVCに向けて自社をプレゼンテーションします",
  "Keep the Momentum Going": "勢いを継続",
  "Additional four weeks to continue building, fundraising, and deepening ecosystem connections in Silicon Valley": "シリコンバレーでさらに4週間、開発、資金調達、エコシステムとの関係構築を続けられます",
  "Growth Accelerator": "グロース・アクセラレーター",
  "For Investor-Ready Startups": "投資を受ける準備が整ったスタートアップ向け",
  "10 Weeks (+4 Extension)": "10週間（＋延長4週間）",
  "Up to $70,000": "最大7万ドル",
  "3.5% target equity": "目標株式3.5%",
  "Up to 10 teams": "最大10チーム",
  "The \"Apex\" Growth Accelerator is a 10-week immersive Silicon Valley program designed for 10 investor-ready startups building across RWA, Institutional DeFi, and enterprise solutions. Selected teams receive up to $70,000 in investment for a target of 3.5% equity, full residency at Draper University, and access to world-class founders, operators, and investors. The program concludes with a dedicated Demo Day, giving startups the opportunity to pitch to investors.":
    "「Apex」グロース・アクセラレーターは、RWA、機関投資家向けDeFi、エンタープライズ・ソリューションに取り組む、投資を受ける準備が整ったスタートアップ10社を対象とした、シリコンバレーでの10週間の没入型プログラムです。選抜チームは目標株式3.5%に対して最大7万ドルの投資、Draper Universityでのフルレジデンシー、世界トップクラスの創業者・オペレーター・投資家へのアクセスを得られます。プログラムの最後には専用のデモデーがあり、投資家にピッチする機会があります。",
  "Receive up to $70,000 in investment plus full residency at Draper University in Silicon Valley": "最大7万ドルの投資に加え、シリコンバレーのDraper Universityでのフルレジデンシーを提供",
  "Investor Access": "投資家へのアクセス",
  "Build relationships with Silicon Valley investors, venture funds, and ecosystem partners": "シリコンバレーの投資家、ベンチャーファンド、エコシステム・パートナーとの関係を築きます",
  "Fundraising & Growth": "資金調達と成長",
  "Refine your fundraising narrative, growth strategy, and go-to-market approach with experienced operators and investors": "経験豊富なオペレーターや投資家と共に、資金調達のストーリー、成長戦略、市場投入のアプローチを磨きます",
  "Mentorship": "メンタリング",
  "Learn directly from world-class founders, operators, and investors with experience scaling startups globally": "スタートアップをグローバルに成長させた経験を持つ、世界トップクラスの創業者、オペレーター、投資家から直接学びます",
  "Demo Day": "デモデー",
  "Pitch your startup to a curated audience of Web3 investors": "厳選されたWeb3投資家に向けて自社をピッチします",
  "01 — Who are we looking for?": "01 — 求める人材",
  "If you're building a Web3 startup in any of these verticals or anything else and would like to hear from us, we'd love to hear from you.":
    "これらの分野、あるいはそれ以外でWeb3スタートアップを築いている方は、ぜひご連絡ください。",
  "Decentralized Media": "分散型メディア",
  "Identity & Privacy": "アイデンティティとプライバシー",
  "Developer Tools": "開発者ツール",
  "Smart Contracts": "スマートコントラクト",
  "We invest globally. You do not have to be based in the USA to qualify. Everyone is encouraged to apply. Please note that this is an in-person program, so you must be eligible for a US visa.":
    "私たちはグローバルに投資しています。米国在住である必要はなく、どなたでも応募を歓迎します。なお、本プログラムは対面形式のため、米国ビザの取得資格が必要です。",
  "02 — Past speakers": "02 — 過去の登壇者",
  "Network and learn from the people": "次世代のオンチェーン技術を",
  "building and funding generational onchain technology": "築き、支える人々とつながり、学ぶ",
  "03 — Past Cardano startups accelerated": "03 — これまでに支援したCardanoスタートアップ",
  "COHORT 1 · 2026": "第1期 · 2026",
  "companies": "社",
  "04 — Application process": "04 — 応募の流れ",
  "How to apply?": "応募方法",
  "If you have questions or concerns, email us at": "ご質問やご不明点は、次のアドレスまでメールでお問い合わせください：",
  "Apply now": "今すぐ応募",
  "Submit your application online": "オンラインで応募書類を提出",
  "Our team will review all applications and email you with the next steps if we decide to move forward with more interviews.":
    "すべての応募書類を審査し、面接に進む場合は次のステップをメールでご案内します。",
  "Are there any fees? What is covered?": "費用はかかりますか？何が含まれますか？",
  "There are no fees to participate in the Cardano x DraperU Genesis Pre Accelerator. However, you will need to pay for your own transportation (flights, rideshare, etc.) and meals. We will provide accommodation and occasional meals, which will be mentioned in the program calendar.":
    "Cardano x DraperU Genesisプレ・アクセラレーターへの参加費はかかりません。ただし、交通費（航空券、ライドシェアなど）と食費は自己負担となります。宿泊と一部の食事はこちらで提供し、プログラムの日程表でお知らせします。",
  "Is this program in person or remote? What language is it taught in?": "対面ですか、リモートですか？使用言語は何ですか？",
  "This program requires all participants to be in-person at Draper University (San Mateo, CA) for the duration of the program.":
    "本プログラムでは、期間中すべての参加者がDraper University（カリフォルニア州サンマテオ）に対面で参加する必要があります。",
  "Can I build on multiple chains?": "複数のチェーンで開発してもよいですか？",
  "This program is in partnership with Draper Dragon's Orion Fund, which aims to back builders on Cardano. You must be building on Cardano as your main chain, although multi-chain projects will be considered.":
    "本プログラムは、Cardano上のビルダーを支援するDraper DragonのOrion Fundとの提携によるものです。メインチェーンとしてCardanoで開発している必要がありますが、マルチチェーンのプロジェクトも検討対象となります。",
  "This program is also a good opportunity for builders to implement Cardano further into projects, which may be accepted with milestones in place.":
    "本プログラムは、プロジェクトへのCardano導入をさらに進める好機でもあり、マイルストーンを設定したうえで採択される場合があります。",
  "Do we get an investment? What are the terms?": "投資は受けられますか？条件は？",
  "Not all companies are guaranteed to receive an investment from the Orion Fund. After being selected for the Pre Accelerator, we will put you through a 4-week diligence period where our investment team will make final decisions.":
    "すべての企業がOrion Fundからの投資を受けられるとは限りません。プレ・アクセラレーターに選ばれた後、4週間のデューデリジェンス期間を経て、投資チームが最終判断を行います。",
  "Selected teams may receive up to $20,000 in investment for a target of 2% equity.": "選抜チームは、目標株式2%に対して最大2万ドルの投資を受けられる場合があります。",
  "Is food provided? Do I have to organize my own food?": "食事は提供されますか？自分で手配する必要がありますか？",
  "Some meals will be provided during catered networking events. For day-to-day meals, you'll need to make your own arrangements. If you need additional support, feel free to reach out—we'll do our best to help throughout the program.":
    "ケータリング付きのネットワーキングイベントでは一部の食事を提供します。日々の食事はご自身で手配してください。サポートが必要な場合はお気軽にご連絡ください。プログラム期間中、できる限りお手伝いします。",
  "Do I need to be based in the USA to qualify?": "応募するには米国在住である必要がありますか？",
  "No, you don't need to be located in the USA to apply. We welcome applicants from all over the world. However, you must be eligible for a US visa. Our team will assist you with support letters and necessary documentation to help with this process.":
    "いいえ、米国在住である必要はありません。世界中からの応募を歓迎します。ただし、米国ビザの取得資格が必要です。手続きに必要なサポートレターや書類の準備はチームがお手伝いします。",

  // ── Team ───────────────────────────────────────────────────────────────
  "Meet the": "",
  "team": "チーム紹介",
  "Investment, growth and acceleration — the teams behind the Orion Fund, backed by the global Draper network.":
    "投資、グロース、アクセラレーション — グローバルなDraperネットワークに支えられた、Orion Fundを担うチームです。",
  "INVESTMENT TEAM": "投資チーム",
  "The investors behind the Orion Fund's direct investments and growth capital.": "Orion Fundの直接投資とグロース資金を担う投資チーム。",
  "GROWTH TEAM": "グロース・チーム",
  "Draper Dragon's in-house venture studio of founders, builders and operators.": "創業者、ビルダー、オペレーターで構成される、Draper Dragonの社内ベンチャースタジオ。",
  "Cometa Labs venture studio team profiles are coming soon.": "Cometa Labsのチームプロフィールは近日公開予定です。",
  "ACCELERATION TEAM": "アクセラレーション・チーム",
  "The Draper University team running the Genesis and Apex Cardano accelerator programs.": "GenesisとApexのCardanoアクセラレーター・プログラムを運営するDraper Universityのチーム。",
  "Building on Cardano?": "Cardanoで開発していますか？",
  "Partner": "パートナー",
  "Principal": "プリンシパル",
  "Investor": "インベスター",
  "Founding Partner": "創業パートナー",
  "Steward": "スチュワード",
  "Venture Partner": "ベンチャー・パートナー",
  "Controller": "コントローラー",
  "Chief Executive Officer": "最高経営責任者（CEO）",
  "Chief Operating Officer": "最高執行責任者（COO）",
  "Chief Commercial Officer": "最高商務責任者（CCO）",
  "Chief Technology Officer": "最高技術責任者（CTO）",
  "Chief Systems Architect": "チーフ・システムアーキテクト",
  "Senior Software Engineer": "シニア・ソフトウェアエンジニア",
  "CEO & Partner": "CEO兼パートナー",
  "VP - Venture Ecosystem": "VP（ベンチャー・エコシステム）",
  "Program Director": "プログラム・ディレクター",
  "Ecosystem & Partnerships Associate": "エコシステム＆パートナーシップ・アソシエイト",
  "Program Operations Coordinator": "プログラム・オペレーション・コーディネーター",
  "Developer": "デベロッパー",

  // ── Media ──────────────────────────────────────────────────────────────
  "Read our": "",
  "Insights and Updates": "インサイトと最新情報",
  "Stay informed with the latest announcements, portfolio news and editorial insights from the Orion Fund.":
    "Orion Fundからの最新のお知らせ、ポートフォリオのニュース、論考をお届けします。",
  "Insights": "インサイト",
  "Articles are unavailable right now.": "現在、記事を表示できません。",
  "New articles are on the way.": "新しい記事を準備中です。",
  "Please check back shortly.": "しばらくしてから再度ご確認ください。",
  "Follow us on X for the latest updates in the meantime.": "それまでの間、最新情報はXでご確認ください。",
  "@OrionFund on X": "@OrionFund（X）",
  "Articles pages": "記事ページ",
  "of": "/",
  "Previous page": "前のページ",
  "Next page": "次のページ",
  "All articles": "すべての記事",
  "Article not found": "記事が見つかりません",
  "This article is unavailable right now": "現在この記事を表示できません",
  "It may have been moved or unpublished.": "移動または非公開になった可能性があります。",
  "Orion Fund Team": "Orion Fund チーム",
  "Read the full story": "全文を読む",

  // ── Contact / apply / feedback forms ──────────────────────────────────
  "Apply": "応募",
  "Intake Form": "エントリーフォーム",
  "Draper Dragon's proposed Orion Ecosystem Fund is designed to invest in and support the next wave of Cardano builders. We're actively building our pipeline across RWA, DeFi, payments, and consumer applications.":
    "Draper Dragonが計画するOrion Ecosystem Fundは、次世代のCardanoビルダーへの投資と支援を目的としています。RWA、DeFi、決済、コンシューマー向けアプリケーションの分野で、積極的に案件を発掘しています。",
  "If you're building on Cardano, considering it, and looking to raise capital, fill out this form and our team will be in touch.":
    "Cardanoで開発中、または導入を検討中で資金調達をお考えの方は、こちらのフォームにご記入ください。チームからご連絡します。",
  "Project Details": "プロジェクトの詳細",
  "Tell us more about your project and its focus.": "プロジェクトの内容と注力分野を教えてください。",
  "Project Name": "プロジェクト名",
  "Brief Description": "概要",
  "One or two sentences on what you're building.": "取り組んでいる内容を1〜2文で。",
  "Website": "ウェブサイト",
  "Vertical(s)": "分野",
  "Select all that apply.": "該当するものをすべて選択してください。",
  "Stage": "ステージ",
  "Cardano Relationship": "Cardanoとの関係",
  "Traction & Funding": "トラクションと資金調達",
  "Share your key metrics and funding requirements.": "主要な指標と必要な資金を教えてください。",
  "Key Traction Metrics": "主要なトラクション指標",
  "Users, TVL, revenue, volume, partnerships…": "ユーザー数、TVL、売上、取引量、提携など…",
  "Funding Ask": "希望調達額",
  "e.g. $500K on a SAFE at an $8M cap": "例：評価上限800万ドルのSAFEで50万ドル",
  "Use of Funds": "資金の使途",
  "Prior Fundraising": "過去の資金調達",
  "Team & Contact Information": "チームと連絡先",
  "Help us understand your team and main point of contact.": "チーム構成と主な連絡窓口を教えてください。",
  "Core Team Size": "コアチームの人数",
  "Primary Contact — Name": "主担当者 — 氏名",
  "Primary Contact — Email": "主担当者 — メールアドレス",
  "Telegram or Discord Handle": "TelegramまたはDiscordのID",
  "Supporting Materials": "補足資料",
  "Additional materials and how you heard about us.": "補足資料と、当ファンドを知ったきっかけ。",
  "Pitch Deck Link": "ピッチ資料のリンク",
  "DocSend, Google Drive or a PDF link": "DocSend、Google DriveまたはPDFのリンク",
  "Whitepaper Link": "ホワイトペーパーのリンク",
  "Additional Notes": "その他",
  "Any other information you'd like to share.": "その他、共有したい情報があればご記入ください。",
  "More Info": "その他の情報",
  "How Did You Hear About Us?": "当ファンドを知ったきっかけ",
  "If Referral — who referred you?": "紹介の場合 — 紹介者のお名前",
  "Please complete the": "次の",
  "highlighted field": "強調表示された項目にご記入ください",
  "Submit": "送信",
  "Submitting…": "送信中…",
  "Thanks for submitting!": "ご応募ありがとうございます！",
  "We've received your application for": "次のプロジェクトの応募を受け付けました：",
  ". The Orion Fund team reviews every submission and will reach out at": "。Orion Fundチームはすべての応募を確認しており、ご縁がありそうな場合は",
  "if there's a fit.": "宛てにご連絡します。",
  "Submit another project": "別のプロジェクトを応募する",
  "Orion Feedback Form": "Orion フィードバックフォーム",
  "Share": "",
  "your thoughts": "ご意見をお聞かせください",
  "Tell us what improvements you would like to see from the Orion Fund team.": "Orion Fundチームに改善してほしい点を教えてください。",
  "Name": "お名前",
  "Email": "メールアドレス",
  "What would you like to see improved?": "どのような改善をご希望ですか？",
  "Name is required.": "お名前を入力してください。",
  "Email is required.": "メールアドレスを入力してください。",
  "Please enter a valid email.": "有効なメールアドレスを入力してください。",
  "Feedback is required.": "フィードバックを入力してください。",
  "Please complete the verification below.": "以下の認証を完了してください。",
  "Something went wrong. Please try again.": "エラーが発生しました。もう一度お試しください。",
  "Received": "受付完了",
  "Thanks for your feedback!": "フィードバックをありがとうございます！",
  "The Orion Fund team reads every message. We appreciate you helping us improve.": "Orion Fundチームはすべてのメッセージに目を通しています。改善へのご協力に感謝します。",
  "Send more feedback": "さらにフィードバックを送る",
  "Sending…": "送信中…",
  "Clear form": "フォームをクリア",

  // ── 404 ────────────────────────────────────────────────────────────────
  "This page": "このページは",
  "doesn't exist": "存在しません",
  "The link may be broken, or the page may have moved.": "リンクが壊れているか、ページが移動した可能性があります。",
  "Back to home": "ホームに戻る",
  "Read our updates": "最新情報を読む",
  // ── Added after on-page review ─────────────────────────────────────────
  "01 — FUNDING": "01 — 投資",
  "02 — GROWTH": "02 — グロース",
  "03 — ACCELERATION": "03 — アクセラレーション",
  "This dashboard is designed to provide public visibility into ecosystem-level progress, capital deployment, and on-chain metrics. Detailed, auditable fund-level financial performance is reported separately to the SPV Directors (including the Community Director) as part of formal oversight and governance processes, in accordance with investor obligations and regulatory requirements.":
    "本ダッシュボードは、エコシステム全体の進捗、資金の投下状況、オンチェーン指標を公開することを目的としています。監査可能なファンドレベルの詳細な財務実績は、投資家への義務および規制要件に従い、正式な監督・ガバナンスの一環として、SPVの取締役（コミュニティ・ディレクターを含む）に別途報告されます。",
  "Leave this empty": "この欄は空欄のままにしてください",
  // Intake form options (config/intakeForm.js)
  "Gaming": "ゲーム",
  "DePIN": "DePIN",
  "Identity": "アイデンティティ",
  "Idea": "アイデア段階",
  "Pre-seed": "プレシード",
  "Seed": "シード",
  "Series A": "シリーズA",
  "Series B+": "シリーズB以降",
  "Live on Cardano": "Cardanoで稼働中",
  "Building on Cardano (not launched yet)": "Cardanoで開発中（未ローンチ）",
  "Multi-chain, including Cardano": "Cardanoを含むマルチチェーン",
  "Considering Cardano": "Cardanoを検討中",
  "Not on Cardano yet": "まだCardanoは未導入",
  "None / bootstrapped": "なし／自己資金",
  "Friends & family": "友人・家族から",
  "Grants (e.g. Project Catalyst)": "助成金（Project Catalystなど）",
  "Angel round": "エンジェルラウンド",
  "Pre-seed round": "プレシードラウンド",
  "Seed round": "シードラウンド",
  "Series A or later": "シリーズA以降",
  "Referral": "紹介",
  "Event or conference": "イベント・カンファレンス",
  "Cardano community": "Cardanoコミュニティ",
  "Search": "検索",
  "Select…": "選択してください…",
  "Select one": "1つ選択してください",
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_RE = MONTHS.join('|');

/** Text built from variables, matched by pattern. Each entry: [RegExp, (match) => Japanese]. */
export const JA_PATTERNS = [
  // "July 13th 2026" → "2026年7月13日"
  [new RegExp(`^(${MONTH_RE}) (\\d{1,2})(?:st|nd|rd|th)?,? (\\d{4})$`), (m) => `${m[3]}年${MONTHS.indexOf(m[1]) + 1}月${Number(m[2])}日`],
  // "Starts October 12th" → "10月12日開始"
  [new RegExp(`^Starts (${MONTH_RE}) (\\d{1,2})(?:st|nd|rd|th)?$`), (m) => `${MONTHS.indexOf(m[1]) + 1}月${Number(m[2])}日開始`],
  // "Latest · Sep 26" → "最新 · Sep 26"
  [/^Latest · (.+)$/, (m) => `最新 · ${m[1]}`],
  // "Up to 15 teams"
  [/^Up to (\d+) teams$/, (m) => `最大${m[1]}チーム`],
  // "Avg. daily active wallets · Sep 2026"
  [/^Avg\. daily active wallets · (.+)$/, (m) => `1日平均アクティブウォレット数 · ${m[1]}`],
  // "· Sep 2026" (month suffix on the active-users box)
  [/^· (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{4})$/, (m) => `· ${m[2]}年${'JanFebMarAprMayJunJulAugSepOctNovDec'.indexOf(m[1]) / 3 + 1}月`],
  // Image alt / button labels built from a company name
  [/^(.+) logo$/, (m) => `${m[1]} ロゴ`],
  [/^(.+) details$/, (m) => `${m[1]} の詳細`],
  // "Funding: Capital for ambitious founders" (aria labels joining two known phrases)
  [/^([^:]+): (.+)$/, (m) => (JA[m[1]] != null && JA[m[2]] != null ? `${JA[m[1]]}：${JA[m[2]]}` : null)],
  // "Name is required." style form errors: "Project Name is required."
  [/^(.+) is required\.$/, (m) => `${JA[m[1]] ?? m[1]}は必須です。`],
];
